import React from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth } from 'date-fns';
import { Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DisplayableItem } from './types';

interface MonthCalendarProps {
  month: Date;
  events: DisplayableItem[];
  isCurrentDay: (date: Date) => boolean;
  getEventsForDate: (date: Date) => DisplayableItem[];
  getPriorityColor: (priority: 'LOW' | 'MEDIUM' | 'HIGH') => string;
  newEventId?: string | null;
  showEventSparkles?: boolean;
}

const WEEKDAYS = [
  { key: 'sunday', label: 'Sun' },
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
];

export const MonthCalendar: React.FC<MonthCalendarProps> = ({ 
  month, 
  events,
  isCurrentDay,
  getEventsForDate,
  getPriorityColor,
  newEventId,
  showEventSparkles
}) => {
  const days = eachDayOfInterval({
    start: startOfMonth(month),
    end: endOfMonth(month)
  });

  // Debug logging
  console.log('🌟 MonthCalendar render:', {
    newEventId,
    showEventSparkles,
    eventsCount: events.length,
    monthLabel: format(month, 'MMMM yyyy')
  });

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm h-full flex flex-col overflow-hidden">
      <div className="text-center p-4 lg:p-6 bg-gray-50 border-b border-gray-200">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
          {format(month, 'MMMM yyyy')}
        </h2>
      </div>
      
      <div className="grid grid-cols-7 gap-px flex-1 overflow-y-auto bg-gray-100">
        {WEEKDAYS.map(day => (
          <div key={day.key} className="text-center text-sm lg:text-base font-semibold text-gray-700 
            py-3 lg:py-4 bg-gray-50 border-b border-gray-200">
            {day.label}
          </div>
        ))}
        
        {days.map(day => {
          const dayItems = getEventsForDate(day);
          const hasNewEvent = dayItems.some(item => item.id === newEventId);
          
          // Debug logging for each day
          if (dayItems.length > 0) {
            console.log('🌟 Day with events:', {
              day: format(day, 'yyyy-MM-dd'),
              dayItems: dayItems.map(item => ({ id: item.id, title: item.title })),
              newEventId,
              hasNewEvent,
              showEventSparkles
            });
          }
          
          return (
            <div
              key={day.toISOString()}
              className={`
                relative h-28 lg:h-32 p-2 bg-white
                hover:bg-gray-50 transition-all duration-200
                ${isCurrentDay(day) ? 'bg-blue-50 ring-1 ring-blue-200' : ''}
                ${hasNewEvent && showEventSparkles 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 ring-2 ring-green-300 shadow-lg' 
                  : ''}
              `}
            >
              {/* Sparkle animations for new events */}
              <AnimatePresence>
                {hasNewEvent && showEventSparkles && (
                  <>
                    {console.log('🌟 Rendering sparkles for day:', format(day, 'yyyy-MM-dd'))}
                    {/* Corner sparkles */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0 }}
                      className="absolute top-1 right-1 z-10"
                    >
                      <motion.div
                        animate={{ 
                          rotate: 360,
                          scale: [1, 1.2, 1]
                        }}
                        transition={{ 
                          rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                          scale: { duration: 1, repeat: Infinity }
                        }}
                      >
                        <Sparkles className="w-4 h-4 text-green-600" />
                      </motion.div>
                    </motion.div>

                    {/* Floating sparkle 1 */}
                    <motion.div
                      initial={{ opacity: 0, x: 10, y: 10 }}
                      animate={{ 
                        opacity: [0, 1, 0],
                        x: [10, -5, 15, 0],
                        y: [10, 5, -10, 10],
                        rotate: 360
                      }}
                      transition={{ 
                        duration: 4,
                        repeat: Infinity,
                        delay: 0.5
                      }}
                      className="absolute top-2 left-2 z-10"
                    >
                      <Sparkles className="w-3 h-3 text-emerald-500" />
                    </motion.div>

                    {/* Floating sparkle 2 */}
                    <motion.div
                      initial={{ opacity: 0, x: -10, y: -10 }}
                      animate={{ 
                        opacity: [0, 1, 0],
                        x: [-10, 10, -5, 0],
                        y: [-10, -5, 10, -10],
                        rotate: -360
                      }}
                      transition={{ 
                        duration: 3,
                        repeat: Infinity,
                        delay: 1
                      }}
                      className="absolute bottom-2 right-3 z-10"
                    >
                      <Sparkles className="w-3 h-3 text-green-500" />
                    </motion.div>

                    {/* Pulsing glow effect */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ 
                        opacity: [0, 0.3, 0],
                        scale: [1, 1.05, 1]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity
                      }}
                      className="absolute inset-0 bg-gradient-to-br from-green-200/40 to-emerald-200/40 rounded-lg pointer-events-none"
                    />
                  </>
                )}
              </AnimatePresence>

              <div className={`
                relative z-20 text-sm lg:text-base font-semibold mb-2 
                ${isCurrentDay(day) 
                  ? 'bg-blue-600 text-white w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-xs lg:text-sm' 
                  : 'text-gray-700'}
              `}>
                {format(day, 'd')}
              </div>
              <div className="relative z-20 space-y-1 max-h-[calc(100%-2.5rem)] lg:max-h-[calc(100%-3rem)] overflow-y-auto">
                {dayItems.map(item => {
                  const isNewEvent = item.id === newEventId;
                  return (
                    <div
                      key={item.id}
                      className={`relative text-xs px-2 py-1 rounded border transition-all duration-200
                        ${isNewEvent && showEventSparkles
                          ? 'bg-gradient-to-r from-green-200 to-emerald-200 border-green-400 shadow-md ring-1 ring-green-300 scale-105 font-bold'
                          : `${getPriorityColor(item.priority)} hover:scale-105`
                        }`}
                    >
                      {/* Extra sparkle on the new event itself */}
                      {isNewEvent && showEventSparkles && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ 
                            opacity: [0, 1, 0],
                            scale: [0, 1, 0],
                            rotate: 360
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            delay: 0.2
                          }}
                          className="absolute -top-1 -right-1"
                        >
                          <Sparkles className="w-3 h-3 text-green-600" />
                        </motion.div>
                      )}

                      <div className="flex items-center gap-1">
                        <span className="font-medium truncate">
                          {item.isTask ? '📋 ' : '📅 '}{item.title}
                        </span>
                      </div>
                      {!item.isAllDay && item.startTime && (
                        <div className="text-xs opacity-75 mt-0.5">
                          {format(new Date(item.startTime), 'h:mm a')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};